import React from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import { Activity } from 'types/interfaces/activity';
import { formatAccessibleTime, formatRelativeTime } from 'utils/relativeTime';
import { ActivityUserGroup } from '../groupActivity';
import ActivityFeedItem from './ActivityFeedItem';
import './ActivityTradeGroup.css';

interface ActivityTradeGroupProps {
  group: ActivityUserGroup;
  /** CSS width (px) for each tile — driven by the S/M/L view mode (GROUPED_TILE_WIDTH). */
  tileWidth: number;
  onSend?: (activity: Activity) => void;
}

/**
 * "By trader" view: every activity item from one user in a single section inside
 * the page's one glass plane (Asana 1219008751039721, decision 13). No glass of
 * its own: sections are separated by a hairline. The header shows the standard
 * identity block once; the tiles are the same card block as By card.
 */
export const ActivityTradeGroup: React.FC<ActivityTradeGroupProps> = ({ group, tileWidth, onSend }) => (
  <section className="activity-group" data-testid="activity-group">
    <div className="activity-group-header">
      <Link className="activity-group-user" to={`/profile/u/${encodeURIComponent(group.user.username)}`}>
        <AvatarWithFlag
          avatarUrl={group.user.avatarUrl}
          username={group.user.username}
          size={34}
          countryCode={group.user.countryCode}
          isElite={group.user.isElite}
          showAnimatedBorder={group.user.showAnimatedBorder}
          borderColor1={group.user.borderColor1}
          borderColor2={group.user.borderColor2}
        />
        <span className="activity-group-user-info">
          <span className="activity-group-user-name">
            {group.user.username}
            {group.user.isElite && <EliteBadge isElite size={14} />}
          </span>
          {group.user.points != null && (
            <span className="activity-group-user-points">
              <Points points={group.user.points} type="small" />
            </span>
          )}
        </span>
      </Link>
      <span className="activity-group-meta">
        {group.count} {group.count === 1 ? 'card' : 'cards'} wanted ·{' '}
        <time dateTime={group.mostRecent} title={formatAccessibleTime(group.mostRecent)}>
          {formatRelativeTime(group.mostRecent)}
        </time>
      </span>
    </div>

    <div className="activity-group-cards" style={{ '--tile': `${tileWidth}px` } as React.CSSProperties}>
      {group.activities.map((activity) => (
        <ActivityFeedItem key={activity.id} activity={activity} grouped onSend={onSend} />
      ))}
    </div>
  </section>
);

export default ActivityTradeGroup;
